import {GridWrapper, h4, mediaQueries, colors as baseColors} from '@theme/lib';
import React, {ReactElement, useState} from 'react';
import styled, {css} from 'styled-components';

import {AccordionParagraph, AccordionTabParagraph, HubRichTextBackground, Maybe, WYSIWYGFragment} from '@/__generated__/graphql';
import {getLanguage} from '@/lib/config';
import WYSIWYG from '../WYSIWYG';
import {isBicentenary} from "@lib/utils";


const AccordionWrapper = styled(GridWrapper)`
  .accordion-item {
    border-radius: ${props => props.theme.config.accordionRadius};

    &:not(:first-child) {
      margin-top: 0.5rem;
    }

    &--closed {
      .accordion-label {
        &::after {
          -webkit-mask-image: url(/Accordion/arrow-down.svg);
        }

        &:hover {
          text-decoration: ${(props) => isBicentenary(props.theme.key) ? 'underline' : 'none'};
          color: ${(props) => props.theme.colors.accordionIconColour};
          border-color: ${(props) => props.theme.colors.accordionTabFocusColour};
        }
      }

      .accordion-content {
        display: none;
      }
    }

    &--opened {
      .accordion-label {
        border-radius: ${props => `${props.theme.config.accordionRadius} ${props.theme.config.accordionRadius} 0 0`};
        border-color: ${(props) => props.theme.colors.accordionTabBorderColour};
        border-width: 1px 1px 0 1px;
        border-style: solid;
        background-color: transparent;
        color: ${(props) => props.theme.colors.accordionTabColourOpened};

        &:hover, &:focus-visible {
          color: ${(props) =>
            isBicentenary(props.theme.key) ? baseColors.honeyBee : props.theme.colors.link};
        }

        &::after {
          -webkit-mask-image: url(/Accordion/arrow-up.svg);
        }

        ${(props) =>
          props.theme.key === 'bicentenary' &&
          css`
            &:hover, &:focus-visible {
              color: ${baseColors.seagrass};
              text-decoration: underline;
            }

            &::after {
              background-color: ${baseColors.seagrass};
            }
          `}
      }
    }
  }
}

.accordion-label {
  ${h4};
  background: ${(props) => props.theme.colors.accordionTabBgr};
  border-radius: ${props => props.theme.config.accordionRadius};
  border: ${(props) => props.theme.key === 'zsl' ? '0px' : '3px'} solid transparent;
  color: ${(props) => props.theme.colors.accordionTabColour};
  display: block;
  padding: 1rem 2.75rem 1rem 1rem;
  position: relative;

  &:hover, &:focus-visible {
    text-decoration: ${(props) => isBicentenary(props.theme.key) ? 'underline' : 'none'};
  }

  ${(props) =>
    props.theme.key === 'bicentenary' &&
    css`
      &:focus-visible {
        color: ${baseColors.honeyBee};
      }
    `}
  &::after {
    background-color: ${(props) => props.theme.colors.accordionIconColour};
    content: "";
    width: 16px;
    height: 9px;
    position: absolute;
    top: calc(50% - 4.5px);
    right: 1.53125rem;
  }
}

.accordion-content {
  color: ${(props) => props.theme.colors.accordionTabColour};
  border-radius: ${props => `0 0 ${props.theme.config.accordionRadius} ${props.theme.config.accordionRadius}`};
  border-color: ${(props) => props.theme.colors.accordionTabBorderColour};
  border-width: 0 1px 1px 1px;
  border-style: solid;
  padding: 0 1.5rem 1.5rem 1rem;

  a:not(.btn) {
    color: ${(props) => isBicentenary(props.theme.key) ? baseColors.seagrass : 'inherit'};
  }

  ul, ol {
    list-style-position: outside;
    padding-left: 1rem;

    li:first-child > :first-child {
      margin-top: 0;
    }
  }
}

${mediaQueries.md} {
  .accordion-label {
    font-size: 1.5rem;
    line-height: 1.8rem;
    padding: 1rem 3.5rem 1rem 1.5rem;

    &::after {
      right: 1.6875rem;
    }
  }

  .accordion-content {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
`;

interface AccordionTabProps {
  title: string;
  closeTitle?: string;
  content: {
    processed: string;
    format: string,
    value: string,
  }
  state?: {
    opened: boolean;
  }
}

interface AccordionProps {
  alignment: string;
  tabs: Array<AccordionTabProps>;
}

interface AccordionParagraphProps extends AccordionParagraph {
  tabs: Array<AccordionTabParagraph & {
    closeTitle?: string;
    field_hub_rich_text_background?: Maybe<HubRichTextBackground>;
   }>
}

export default function Accordion({alignment, tabs}: AccordionParagraphProps): ReactElement<any> {
  const [openedStates, setOpenedStates] = useState<number[]>([]);
  const language = getLanguage();

  const toggleTabState = (e: React.MouseEvent<HTMLElement>, tabNum: number) => {
    e.preventDefault();

    let states = [...openedStates];
    if (!openedStates.includes(tabNum)) {
      states.push(tabNum);
    } else {
      states = openedStates.filter((_tabNum) => _tabNum !== tabNum);
    }

    setOpenedStates(states);
  };

  return <AccordionWrapper alignment={alignment}>
    {tabs.map((tab, key) =>
      <div key={key}
           className={`accordion-item ${openedStates.includes(key) ? 'accordion-item--opened' : 'accordion-item--closed'}`}>
        <a className="accordion-label" href={`#item-${key + 1}`} onClick={e => toggleTabState(e, key)}
           lang={language !== 'en' ? language : undefined}>{tab?.closeTitle && openedStates.includes(key) ? tab?.closeTitle : tab.title}</a>
        <div className="accordion-content">
          <WYSIWYG id={`accordion-${key}`} text={tab.content as WYSIWYGFragment['text']} alignment="left" field_hub_rich_text_background={tab?.field_hub_rich_text_background}/>
        </div>
      </div>
    )}
  </AccordionWrapper>
}
